// utils/oopUtils.ts
type SettingsTier = { name: string; value: string };
type SurgeonAlias = { surgeon_id: number | string; tier: string | null };
type HospitalAlias = { hospital_id: number | string; tier: string | null };

type UserSettingsShape = {
	tiers?: SettingsTier[];
	aliases?: {
		surgeon_aliases?: SurgeonAlias[];
		hospital_aliases?: HospitalAlias[];
	};
};

const num = (v: unknown) => {
	const n = Number(v);
	return Number.isFinite(n) ? n : 0;
};

const findTierValue = (
	tiers: SettingsTier[] | undefined,
	tierName: string | null | undefined
) => {
	if (!tiers || !tierName) return 0;
	const t = tiers.find(
		(x) => String(x.name).trim() === String(tierName).trim()
	);
	return t ? num(t.value) : 0;
};

/**
 * Resolve settings-derived OOP only (no event override here).
 * Sums surgeon + hospital tiers if both are present.
 */
export const resolveOOPFromSettings = (
	ev: any,
	settings?: UserSettingsShape
): { oop: number; source: string; surgeonVal: number; hospitalVal: number } => {
	const tiers = settings?.tiers ?? [];

	// Surgeon (ID only)
	const surgeonId = ev?.surgeon_id ?? null;
	const sTier =
		surgeonId != null
			? settings?.aliases?.surgeon_aliases?.find(
					(s) => String(s.surgeon_id) === String(surgeonId)
			  )?.tier ?? null
			: null;
	const surgeonVal = findTierValue(tiers, sTier);

	// Hospital (ID only)
	const hospitalId = ev?.hospital_id ?? null;
	const hTier =
		hospitalId != null
			? settings?.aliases?.hospital_aliases?.find(
					(h) => String(h.hospital_id) === String(hospitalId)
			  )?.tier ?? null
			: null;
	const hospitalVal = findTierValue(tiers, hTier);

	const total = surgeonVal + hospitalVal;

	const parts: string[] = [];
	if (sTier) parts.push(`Surgeon Tier: ${sTier} (${surgeonVal.toFixed(2)})`);
	if (hTier)
		parts.push(`Hospital Tier: ${hTier} (${hospitalVal.toFixed(2)})`);
	const source = parts.length ? parts.join("\n") : "fallback(0)";

	return { oop: total, source, surgeonVal, hospitalVal };
};

export const toCents = (v: unknown) => {
	if (v === null || v === undefined || v === "") return null;
	const n = Number(v);
	return Number.isFinite(n) ? Math.round(n * 100) : null;
};

// Numeric-equality by cents without needing a helper util:
export const equalMoney = (a: unknown, b: unknown) => {
	const toCentsLocal = (v: unknown): number | null => {
		if (v == null) return null;
		const s = String(v).trim();
		if (!s) return null;
		const n = Number(s);
		if (!Number.isFinite(n)) return null;
		return Math.round(n * 100);
	};
	return toCentsLocal(a) === toCentsLocal(b);
};

export const normaliseCurrency = (s: string) => {
	const clean = (s ?? "").replace(/[^\d.]/g, "");
	if (clean === "") return "";
	const n = Number(clean);
	return Number.isNaN(n) ? "" : n.toFixed(2);
};
